import React from "react";
import { useState, useEffect, useContext, useMemo } from "react";
import { Link } from "react-router-dom";
import { DateObject } from "react-multi-date-picker";
import { NotificationAlertContext } from "contexts/notificationAlertContext";
import Select from "react-select";

// react-bootstrap components
import {
  Button,
  Card,
  Container,
  Row,
  Col,
  Spinner,
  Form,
} from "react-bootstrap";
import CustomMultiDateSelect from "components/Custom/CustomMultiDateSelect";
import CustomStatus from "components/Custom/CustomStatus";
import { getClients, getList } from "graphql/queries.js";
import { API, graphqlOperation } from "aws-amplify";
import {
  convertDateListToString,
  convertDateListToDateRangeString,
} from "utilities/dateUtilities";
import ReactTable from "components/ReactTable/ReactTableWithDynamicPagination";
import { AuthContext } from "contexts/authContext";
import _ from "lodash";
import {
  MultiValueWithCustomTooltip,
  customEllipsisMultiSelectStyles,
} from "components/Custom/CustomMultiSelect.js";

function Clients() {
  // Hooks
  const notify = useContext(NotificationAlertContext);

  // Context
  const currentUser = useContext(AuthContext);

  const environmentOptions = [
    { value: "internal", label: "Internal" },
    { value: "external", label: "External" },
  ];
  const searchByOptions = [
    { value: "name", label: "Client Name" },
    { value: "sapId", label: "SAP ID" },
  ];
  // States
  const [tableData, setTableData] = useState([]);
  const [jobRanAt, setJobRanAt] = useState(null);
  const [showFirstLoader, setShowFirstLoader] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [showError, setShowError] = useState(false);
  const [metricDateList, setMetricDateList] = useState([new DateObject()]);
  const [showInactiveClients, setShowInactiveClients] = useState(false);
  const [selectedEnvironments, setSelectedEnvironments] = useState([
  ]);
  const [searchBy, setSearchBy] = useState(searchByOptions[0]); // default: name

  const headers = [
    { key: "client_status", label: "Client Status" },
    { key: "client_name", label: "Client Name" },
    { key: "client_environments", label: "Client Environments" },
    { key: "channels", label: "Channels" },
    { key: "queued_messages", label: "Queued Messages" },
    { key: "received_messages", label: "Received Messages" },
    { key: "sent_messages", label: "Sent Messages" },
    { key: "errored_messages", label: "Errored Messages" },
    { key: "filtered_messages", label: "Filtered Messages" },
    { key: "avg_process_time", label: "Avg Process Time" },
    { key: "last_calculated", label: "Last Calculated" },
  ];

  // Pagination Starts
  const columnMapping = {
    Name: "client_name",
    Environments: "client_environments",
    "Internal Environments": "internal_environments",
    "External Environments": "external_environments",
    Channels: "channels",
    Queued: "queued_messages",
    Received: "received_messages",
    "Sent/Transformed": "sent_messages",
    Errored: "errored_messages",
    Filtered: "filtered_messages",
    Status: "client_status",
    "Avg Process Time (sec)": "avg_process_time",
  };

  const [totalCount, setTotalCount] = useState(0);
  const numberOfRowsData = [25, 50, 100];

  const [currentLimit, setCurrentLimit] = useState(numberOfRowsData[1]);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [ascOrDescValue, setAscOrDescValue] = useState("ASC");
  const [sortValue, setSortValue] = useState("Name");

  const setStateForPagnination = async (key, value) => {
    if (key === "currentLimit") {
      setCurrentLimit(value);
    } else if (key === "offset") {
      setCurrentOffset(value);
    } else if (key === "ascOrDesc") {
      setAscOrDescValue(value);
    } else {
      setSortValue(value);
    }
  };
  // Pagination Ends

  const [selectedClients, setSelectedClients] = useState([]);
  const [activeClientNameOptions, setActiveClientNameOptions] = useState([]);
  const [activeClientSapIdOptions, setActiveClientSapIdOptions] = useState([]);
  const [inactiveClientNameOptions, setInactiveClientNameOptions] = useState([]);
  const [inactiveClientSapIdOptions, setInactiveClientSapIdOptions] = useState([]);

  const handleIncludeInactiveClients = () => {
    setSelectedClients([]);
    setShowInactiveClients(!showInactiveClients);
  };

  // Build options dynamically based on "searchBy"
  const clientFilterOptions = showInactiveClients
    ? inactiveClientNameOptions.map((c) => ({
      value: c.value,
      label: c.label,
    }))
    : activeClientNameOptions.map((c) => {
      return {
        value: c.value,
        label: c.label,
      };
    });
  const sapIdClientFilterOptions = showInactiveClients
    ? inactiveClientSapIdOptions.map((c) => ({
      value: c.value,
      label: c.label,
    }))
    : activeClientSapIdOptions.map((c) => {
      return {
        value: c.value,
        label: c.label,
      };
    });
  // Fetch options for clients
  const fetchList = async (is_active) => {
    try {
      const response = await API.graphql(
        graphqlOperation(getList, {
          type: "CLIENTS",
          is_active,
        })
      );
      if (response.data?.getList?.success) {
        const clientNameOptions = response.data.getList
          ? response.data.getList.body
            .map((client) => {
              const option = {
                value: client.id,
                label: client.name,
              };
              return option;
            })
            .sort((a, b) => a.label.localeCompare(b.label))
          : [];
        const clientSapIdOptions = response.data.getList
          ? response.data.getList.body
            .map((client) => {
              if (client.sap_id) {
                const option = {
                  value: client.sap_id,
                  label: client.sap_id,
                };
                return option;
              } else {
                return false
              }
            }).filter(Boolean)
            .sort((a, b) => a?.label?.localeCompare(b?.label))
          : [];
        if (is_active) {
          setActiveClientNameOptions(clientNameOptions);
          setActiveClientSapIdOptions(clientSapIdOptions);
        } else {
          setInactiveClientNameOptions(clientNameOptions);
          setInactiveClientSapIdOptions(clientSapIdOptions);
        }
      } else {
        console.error(response.data?.getList?.message);
        notify("danger", `Could not fetch all clients list.`);
      }
      return response.data?.getList?.success;
    } catch (error) {
      console.error(error);
      notify("danger", `Could not fetch all clients list.`);
      return false;
    }
  };

  const fetchClients = async (filterSearchObj, isCSVDownload = false) => {
    let selectedEnvironmentValue = ''
    if (selectedEnvironments?.length === 1) {
      selectedEnvironmentValue = selectedEnvironments[0].value
    } else if (selectedEnvironments?.length === 2) {
      selectedEnvironmentValue = 'both'
    }
    try {
      setShowLoader(true);
      const metricDates = {
        metric_date_range_list:
          convertDateListToDateRangeString(metricDateList),
        ...filterSearchObj,
        is_active: !showInactiveClients,
        env_type_value: selectedEnvironmentValue
      };
      if (searchBy.value == 'sapId') {
        metricDates.sap_ids = selectedClients.map((client) => client.value);
      } else {
        metricDates.client_ids = selectedClients.map((client) => client.value);
      }
      const response = await API.graphql(
        graphqlOperation(getClients, metricDates)
      );
      if (response.data?.getClients?.success) {
        if (!isCSVDownload) {
          const clientsList = response.data.getClients.body.clientsData;
          const totalCount = response.data.getClients.body.totalCount;
          setTableData(clientsList);
          setTotalCount(totalCount);
          if (clientsList.length > 0) {
            setJobRanAt(clientsList[0].last_calculated);
          } else {
            setJobRanAt(null);
          }
        } else {
          const filter = {
            "Metric Date List": convertDateListToString(metricDateList),
          };
          const messageDataForCSV = _.get(
            response,
            "data.getClients.body.clientsData"
          );
          return { messageDataForCSV, filter };
        }
      } else {
        console.error(response.data?.getClients?.message);
      }
      return response.data?.getClients?.success;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setShowLoader(false);
    }
  };

  useEffect(() => {
    async function fetchData() {
      setShowFirstLoader(true);
      const filterObj = {
        offSet: 0,
        limit: 50,
      };
      const promiseAllResults = await Promise.all([
        fetchList(true),
        fetchList(false),
        fetchClients(filterObj),
      ]);
      setShowFirstLoader(false);
      const isSuccess = promiseAllResults.every((value) => value);
      if (!isSuccess) {
        notify("danger");
        setShowError(true);
      }
    }
    fetchData();
  }, []);

  if (showFirstLoader || showLoader) return <Spinner animation="grow" />;

  if (showError)
    return `Could not load the page at this moment. Please try again later.`;

  return (
    <>
      <Container fluid>
        <Row>
          <Col>
            <h2>All Clients</h2>
          </Col>
          {currentUser.groupName === "internal" && (
            <Col className="my-auto text-right">
              <Link
                className="btn btn-primary"
                to={{
                  pathname: "/admin/clients/activate-client",
                }}
              >
                <i className="fas fa-plus"></i> Activate New Client
              </Link>
            </Col>
          )}
        </Row>
        <Row>
          <Col>
            <Card>
              <Card.Header>
                <Card.Title>Metrics Search</Card.Title>
                <Card.Subtitle>
                  {jobRanAt
                    ? `Last calculated at ${new Date(jobRanAt).toLocaleString(
                      "en-US"
                    )}`
                    : `No data to show for selected date(s)`}
                </Card.Subtitle>
              </Card.Header>
              <Card.Body>
                <Row className="align-items-end">
                  <Col md="4" sm="12">
                    <label>Select Date(s):</label>
                    <CustomMultiDateSelect
                      className="react-select primary"
                      classNamePrefix="react-select"
                      style={{ width: "100%" }}
                      value={metricDateList}
                      onChange={setMetricDateList}
                    ></CustomMultiDateSelect>
                  </Col>
                  <Col md="4" sm="12">
                    <label>Search By</label>
                    <Select
                      className="react-select primary"
                      classNamePrefix="react-select"
                      id="searchBy"
                      name="searchBy"
                      closeMenuOnSelect={true}
                      isMulti={false}
                      value={searchBy}
                      onChange={(option) => {
                        setSearchBy(option);
                        setSelectedClients([]); // reset filter when search type changes
                      }}
                      options={searchByOptions}
                      isClearable={false}
                      placeholder="Select Search Type"
                    />
                  </Col>

                  <Col md="4" sm="12">
                    <label>Client Filter</label>
                    <Select
                      className="react-select primary"
                      classNamePrefix="react-select"
                      id="clientFilter"
                      name="clientFilter"
                      closeMenuOnSelect={false}
                      isMulti
                      styles={customEllipsisMultiSelectStyles}
                      components={{ MultiValue: MultiValueWithCustomTooltip }}
                      value={selectedClients}
                      onChange={(clients) => {
                        setSelectedClients(clients || [])
                      }
                      }
                      options={searchBy.value == 'sapId' ? sapIdClientFilterOptions : clientFilterOptions}
                      isClearable={false}
                      placeholder={`Filter by ${searchBy.label}`}
                    />
                  </Col>
                </Row>

                <Row className="align-items-end">
                  <Col md="4" sm="12">
                    <label>Environment Type Filter</label>
                    <Select
                      className="react-select primary"
                      classNamePrefix="react-select"
                      id="selectEnvironment"
                      name="selectEnvironment"
                      closeMenuOnSelect={false}
                      isMulti
                      styles={customEllipsisMultiSelectStyles}
                      components={{ MultiValue: MultiValueWithCustomTooltip }}
                      value={selectedEnvironments}
                      onChange={setSelectedEnvironments}
                      options={environmentOptions}
                      isClearable={false} // disables "x" clear button
                      placeholder="Select environment"
                    />
                  </Col>
                  <Col md="2" sm="6" style={{ marginBottom: "8px" }}>
                    <Form.Check>
                      <Form.Check.Label>
                        <Form.Check.Input
                          type="checkbox"
                          checked={showInactiveClients}
                          onChange={handleIncludeInactiveClients}
                        ></Form.Check.Input>
                        <span className="form-check-sign"></span>
                        Inactive Clients
                      </Form.Check.Label>
                    </Form.Check>
                  </Col>
                  <Col md="1" sm="6">
                    <Button
                      variant="primary"
                      onClick={async () => {
                        setSortValue("Name");
                        setAscOrDescValue("ASC");
                        const filterObj = {
                          offSet: 0,
                          limit: 50,
                        };
                        const isSuccess = await fetchClients(filterObj);
                        if (isSuccess) notify("success");
                        else notify("danger");
                      }}
                    >
                      {" "}
                      Search
                    </Button>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <ReactTable
          data={tableData}
          totalCount={totalCount}
          functionCallBack={fetchClients}
          setStateForPagnination={setStateForPagnination}
          currentLimit={currentLimit}
          currentOffset={currentOffset}
          ascOrDescValue={ascOrDescValue}
          sortValue={sortValue}
          numberOfRowsData={numberOfRowsData}
          columnMapping={columnMapping}
          tableName="Clients"
          CSVHeaders={headers}
          CSVFileName="Clients_Export"
          notify={notify}
          columns={[
            {
              Header: "Status",
              Cell: (cell) => {
                const row = cell.row.original;
                if (row.active)
                  return <CustomStatus value={row.client_status} />;
              },
              accessor: "client_status",
              filterable: false,
              showTooltip: false,
            },
            {
              Header: "Name",
              Cell: (cell) => {
                const row = cell.row.original;
                if (row.active) {
                  return (
                    <Link
                      to={{
                        pathname: `/admin/clients/${row.client_id}`,
                      }}
                    >
                      {row.client_name}
                    </Link>
                  );
                } else {
                  return <span>{row.client_name}</span>;
                }
              },
              accessor: (row) => {
                return row.client_name.toLocaleLowerCase();
              },
              filterable: false,
              showTooltip: true,
              minWidth: 180,
            },
            {
              Header: "Environments",
              accessor: "client_environments",
              filterable: false,
              showTooltip: false,
              minWidth: 160,
            },
            {
              Header: "Internal Environments",
              accessor: "internal_environments",
              filterable: false,
              showTooltip: false,
              minWidth: 160,
            },
            {
              Header: "External Environments",
              accessor: "external_environments",
              filterable: false,
              showTooltip: false,
              minWidth: 160,
            },
            {
              Header: "Channels",
              accessor: "channels",
              filterable: false,
              showTooltip: false,
              maxWidth: 80,
            },
            {
              Header: "Received",
              accessor: (row) => {
                return row.received_messages ? row.received_messages : 0;
              },
              filterable: false,
              showTooltip: false,
              maxWidth: 80,
            },
            {
              Header: "Queued",
              accessor: (row) => {
                return row.queued_messages ? row.queued_messages : 0;
              },
              filterable: false,
              showTooltip: false,
              maxWidth: 80,
            },
            {
              Header: "Filtered",
              accessor: (row) => {
                return row.filtered_messages ? row.filtered_messages : 0;
              },
              filterable: false,
              showTooltip: false,
              maxWidth: 80,
            },
            {
              Header: "Errored",
              accessor: (row) => {
                return row.errored_messages ? row.errored_messages : 0;
              },
              filterable: false,
              showTooltip: false,
              maxWidth: 80,
            },
            {
              Header: "Sent/Transformed",
              accessor: (row) => {
                return row.sent_messages ? row.sent_messages : 0;
              },
              filterable: false,
              showTooltip: false,
              maxWidth: 80,
              minWidth: 160,
            },
            {
              Header: "Avg Process Time (sec)",
              accessor: (row) => {
                return Math.round(10000 * row.avg_process_time) / 10000;
              },
              filterable: false,
              showTooltip: false,
              maxWidth: 80,
              minWidth: 160,
            },
          ]}
        />
      </Container>
    </>
  );
}

export default Clients;
